import React, { useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from 'supabase/supabaseClient';
import icon from '../../assets/pocketwatch-icon-01@2x.png';
import logo from '../../assets/pocketwatch-logo-01.svg';

const DEMO_EMAILS = process.env.REACT_APP_DEMO_EMAILS?.split(',').map(e => e.trim()) ?? [];
const DEMO_PASSWORD = process.env.REACT_APP_DEMO_PASSWORD;

const SignInPage = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Where ProtectedRoute bounced us from, if anywhere.
  const from = location.state?.from
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : '/networth';

  const sendOtp = async () => {
    setError('');
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    });
    setLoading(false);
    if (error) setError(error.message);
    else setOtpSent(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (DEMO_EMAILS.includes(email.toLowerCase()) && DEMO_PASSWORD) {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({ email: email.toLowerCase(), password: DEMO_PASSWORD });
      setLoading(false);
      if (error) setError(error.message);
      else navigate(from, { replace: true });
      return;
    }

    if (email.toLowerCase().endsWith('@pocketwatch.money')) {
      navigate('/private/signin', { state: location.state });
      return;
    }

    await sendOtp();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error } = await supabase.auth.verifyOtp({
      email,
      token: otp.join(''),
      type: 'email',
    });
    setLoading(false);
    if (error) setError(error.message);
    else navigate(from, { replace: true });
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setOtp(paste.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  return (
    <div className="AuthPage">
      <div className="AuthCard">
        <div className="AuthBranding">
          <img src={icon} alt="Pocketwatch" height="48" />
          <img src={logo} alt="Pocketwatch" width="140" />
        </div>

        {otpSent ? (
          <>
            <h1 className="AuthTitle">Check your email</h1>
            <p className="small o50" style={{ textAlign: 'center', marginBottom: '0.8rem' }}>
              We sent a 6-digit code to <strong>{email}</strong>
            </p>

            {error && <div className="AuthError">{error}</div>}

            <form className="AuthForm" onSubmit={handleVerify}>
              <div className="AuthOtp" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    className="AuthOtpInput"
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    autoFocus={i === 0}
                  />
                ))}
              </div>
              <button type="submit" className="button medium AuthSubmit" disabled={loading || otp.join('').length < 6}>
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </form>

            <p className="AuthFooter">
              <button className="AuthLinkButton" onClick={sendOtp} disabled={loading}>Resend code</button>
              {' · '}
              <button className="AuthLinkButton" onClick={() => { setOtpSent(false); setOtp(['', '', '', '', '', '']); setError(''); }}>Wrong email?</button>
            </p>
          </>
        ) : (
          <>
            <h1 className="AuthTitle">Welcome</h1>

            {error && <div className="AuthError">{error}</div>}

            <form className="AuthForm" onSubmit={handleSubmit}>
              <div className="AuthField">
                <label htmlFor="email">Email</label>
                <input
                  type="email" id="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email" required
                />
              </div>
              <button type="submit" className="button medium AuthSubmit" disabled={loading}>
                {loading ? 'Sending code...' : 'Continue'}
              </button>
            </form>

            <Link to="/private/signin" className="AuthPrivateLink" tabIndex={-1}>&nbsp;</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default SignInPage;
